import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { PageHeader } from '@/components/patterns';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, Edit, Trash2, Eye, Copy, DollarSign } from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import { useModelStore } from '../store/model-store';
import { PricingModel } from '../types/model.types';

import { ModelForm } from '../components/model-form';
import { TemplatePickerDialog } from '../components/template-picker-dialog';
import type { ModelTemplate } from '../data/model-templates';

const MODEL_TYPE_LABELS: Record<string, string> = {
  STANDARD: 'Estandar',
  SUBSCRIPTION: 'Suscripcion',
  BUNDLE: 'Bundle / Paquete',
  GOOD_BETTER_BEST: 'Bueno-Mejor-Optimo',
  CUSTOMER_SPECIFIC: 'Especifico por Cliente',
  TCO_ROI: 'TCO / ROI',
  PROMOTIONAL: 'Promocional',
  USAGE_BASED: 'Basado en Uso',
  COST_PLUS: 'Costo + Margen',
  DYNAMIC: 'Dinamico',
  LEASING: 'Leasing',
  MANAGED_SERVICE: 'Servicio Gestionado',
  TIERED_VOLUME: 'Volumen Escalonado',
  FREEMIUM: 'Freemium',
  PROJECT_BASED: 'Por Proyecto',
  HYBRID: 'Hibrido',
  AUCTION: 'Subasta / Licitación',
};

export function ModelsListPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTemplatePickerOpen, setIsTemplatePickerOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ModelTemplate | null>(null);
  const [editingModel, setEditingModel] = useState<PricingModel | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<PricingModel | null>(null);

  const { models, isLoading, fetchModels, deleteModel, cloneModel } = useModelStore();

  useEffect(() => {
    fetchModels();
  }, []);

  const handleCreate = () => {
    setEditingModel(null);
    setSelectedTemplate(null);
    setIsTemplatePickerOpen(true);
  };

  const handleTemplateSelect = (template: ModelTemplate) => {
    setSelectedTemplate(template);
    setIsDialogOpen(true);
  };

  const handleEdit = (model: PricingModel) => {
    setEditingModel(model);
    setIsDialogOpen(true);
  };

  const handleView = (model: PricingModel) => {
    navigate(`/pricing-models/${model.id}`);
  };

  const handleDelete = (model: PricingModel) => {
    setDeleteConfirm(model);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteModel(deleteConfirm.id);
      toast({
        title: 'Modelo eliminado',
        description: 'El modelo ha sido eliminado correctamente',
        variant: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Error al eliminar',
        variant: 'destructive',
      });
    } finally {
      setDeleteConfirm(null);
    }
  };

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] px-4 py-8 md:px-8">
        <div className="space-y-6">
          {/* El encabezado de Cotiza (artefacto «Modelos», 19-oct). */}
          <div data-tour="models-header">
            <PageHeader
              modulo="cotiza"
              etiqueta="Cotiza"
              icono={DollarSign}
              titulo="Modelos"
              subtitulo="Cómo se calcula el precio: las variables y las fórmulas de cada modelo."
              acciones={
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      data-tour="models-new-btn"
                      variant="degradado"
                      size="lg"
                      className="px-5"
                      onClick={handleCreate}
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" />
                      Nuevo modelo
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    Crear un modelo de pricing con variables, fórmulas y márgenes
                  </TooltipContent>
                </Tooltip>
              }
            />
          </div>

          {/* Models Table */}
          <Card>
            <CardHeader>
              <CardTitle>Tus modelos</CardTitle>
              <CardDescription>
                Activos e inactivos. Tocá uno para ver y editar sus variables.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Cargando modelos...</p>
                </div>
              ) : models.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No hay modelos configurados</p>
                  <Button onClick={handleCreate} variant="outline" className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Crear Primer Modelo
                  </Button>
                </div>
              ) : (
                <div data-tour="models-table" className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Código</TableHead>
                        <TableHead>Variables</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {models.map((model) => (
                        <TableRow
                          key={model.id}
                          className="cursor-pointer hover:bg-accent"
                          onClick={() => handleView(model)}
                        >
                          <TableCell>
                            <div className="space-y-1">
                              <span className="font-semibold">{model.name}</span>
                              {model.type && model.type !== 'STANDARD' && (
                                <Badge variant="outline" className="ml-2 text-xs">
                                  {MODEL_TYPE_LABELS[model.type] || model.type}
                                </Badge>
                              )}
                              {model.description && (
                                <p className="text-sm text-muted-foreground line-clamp-2 max-w-md">
                                  {model.description}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono">{model.code}</TableCell>
                          <TableCell>
                            <Badge variant="default">{model.variables.length} variables</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={model.isActive ? 'success' : 'default'}>
                              {model.isActive ? 'Activo' : 'Inactivo'}
                            </Badge>
                          </TableCell>
                          <TableCell
                            data-tour="models-actions"
                            className="text-right"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex justify-end gap-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleView(model)}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Ver detalle del modelo, variables y formulas
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={async () => {
                                      try {
                                        const cloned = await cloneModel(model.id);
                                        toast({ title: 'Modelo clonado', variant: 'success' });
                                        navigate(`/pricing-models/${cloned.id}`);
                                      } catch (error: any) {
                                        toast({
                                          title: 'Error',
                                          description: error.message,
                                          variant: 'destructive',
                                        });
                                      }
                                    }}
                                  >
                                    <Copy className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Duplicar este modelo con todas sus variables y formulas
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEdit(model)}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Editar nombre, código y márgenes del modelo
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDelete(model)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Eliminar este modelo de pricing</TooltipContent>
                              </Tooltip>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el modelo &quot;{deleteConfirm?.name}&quot; de forma permanente junto con
              todas sus variables y configuraciones. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDelete}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Template Picker */}
      <TemplatePickerDialog
        open={isTemplatePickerOpen}
        onOpenChange={setIsTemplatePickerOpen}
        onSelect={handleTemplateSelect}
      />

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingModel ? 'Editar Modelo' : 'Crear Modelo'}</DialogTitle>
            <DialogDescription>
              {editingModel
                ? 'Actualiza la configuración del modelo'
                : selectedTemplate && selectedTemplate.id !== 'custom'
                  ? `Basado en template "${selectedTemplate.name}" con ${selectedTemplate.variables.length} variables`
                  : 'Un modelo nuevo: después le cargás sus variables y fórmulas'}
            </DialogDescription>
          </DialogHeader>
          <ModelForm
            model={editingModel || undefined}
            template={!editingModel ? selectedTemplate : null}
            onSuccess={(createdModelId) => {
              setIsDialogOpen(false);
              setEditingModel(null);
              setSelectedTemplate(null);
              if (createdModelId) {
                navigate(`/pricing-models/${createdModelId}`);
              } else {
                fetchModels();
              }
            }}
            onCancel={() => setIsDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

export default ModelsListPage;
